import React, { Fragment, useState, useEffect } from 'react';

// Component

import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// Utils

import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import validators from '@utils/validators/validators';

// Constants

import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';

const SocialSecurityNumberInput = props => {
    const {
        inputData: {
            id,
            isInputRequired,
            helperClasses,
            name,
            placeholder,
            type,
            value,
            valuesToMatch,
            validationType,
        },
        inputActions: { handleFieldChange },
        label: { text, labelClasses, ariaLabel },
    } = props;

    const [localState, setLocalState] = useState({
        currentSsn: value,
        isHidden: true,
    });
    const WARNING_TYPE = {
        UNIQUE: 'UNIQUE',
        INCORRECT: 'INCORRECT',
    };

    const warningsConfig = {
        [WARNING_TYPE.UNIQUE]: {
            visibility: false,
            warningText: VALIDATION_ERROR_MESSAGES.NOT_UNIQUE_SSN,
        },
        [WARNING_TYPE.INCORRECT]: {
            visibility: false,
            warningText: VALIDATION_ERROR_MESSAGES.INCORRECT_SSN,
        },
    };

    const [warnings, updateWarnings] = useState(warningsConfig);

    const { currentSsn, isHidden } = localState;

    useEffect(() => {
        if (isHidden) {
            setLocalState(state => ({
                ...state,
                currentSsn: value,
            }));
        }
    }, [value, isHidden]);

    const showMessage = type => {
        const toggleWarning = visibility =>
            updateWarnings(warnings => ({
                ...warnings,
                [type]: {
                    ...warnings[type],
                    visibility,
                },
            }));

        toggleWarning(true);

        setTimeout(() => {
            toggleWarning(false);
        }, 3000);
    };

    const handleSsnFocus = () => {
        setLocalState({
            currentSsn: socialSecurityNumberTools.getOnFocus(currentSsn),
            isHidden: false,
        });
    };

    const handleSsnChange = e => {
        const { value: nextSsnValue } = e.target;

        if ((nextSsnValue.length > currentSsn?.length && isNaN(+nextSsnValue.slice(-1))) || nextSsnValue.length > 11) {
            return null;
        }

        const ssnNumberToSave = socialSecurityNumberTools.autoFillSsn(nextSsnValue, currentSsn ?? '');

        setLocalState(state => ({
            ...state,
            currentSsn: ssnNumberToSave,
        }));
    };

    const handleSsnBlur = () => {
        let isSsnNumberValid = false;
        if (validators && validators.find(validator => validator.validatorType === VALIDATION_TYPES[validationType])) {
            isSsnNumberValid = validators
                .find(validator => validator.validatorType === VALIDATION_TYPES[validationType])
                .verifier({ value: socialSecurityNumberTools.cleanedUpSsn(currentSsn) });
        } else {
            isSsnNumberValid = true;
        }

        if (!isSsnNumberValid && currentSsn !== '' && isInputRequired) {
            showMessage(WARNING_TYPE.INCORRECT);
        }

        const isSsnUnique = validators
            .find(validator => validator.validatorType === VALIDATION_TYPES.UNIQUE)
            .verifier({ value: socialSecurityNumberTools.cleanedUpSsn(currentSsn), valuesToMatch: valuesToMatch });

        if (!isSsnUnique) {
            showMessage(WARNING_TYPE.UNIQUE);
        }

        handleFieldChange(isSsnNumberValid && isSsnUnique ? socialSecurityNumberTools.cleanedUpSsn(currentSsn) : null);

        setLocalState(state => ({
            ...state,
            isHidden: true,
        }));
    };

    const getWarnings = () => {
        return Object.keys(warningsConfig).map(type => {
            return (
                <ValidationErrorMessage
                    key={type}
                    isVisible={warnings[type].visibility}
                    messageText={warnings[type].warningText}
                />
            );
        });
    };

    return (
        <Fragment>
            {getWarnings()}
            <div className={`grid-row gi-component gi-${type} ${helperClasses && helperClasses.join(' ')}`}>
                <label
                    htmlFor={id}
                    className={`tablet:grid-col-4 gi-${type}__label ${
                        labelClasses && labelClasses.join(' ')
                    } required-${isInputRequired}`}
                    aria-label={ariaLabel}
                >
                    {text}
                </label>
                <input
                    id={id}
                    className={`tablet:grid-col-6 gi-${type}__input`}
                    data-testid={id}
                    name={name}
                    type="text"
                    value={
                        isHidden
                            ? socialSecurityNumberTools.maskSsn(currentSsn)
                            : socialSecurityNumberTools.getAdjustedSsn(currentSsn)
                    }
                    aria-required={isInputRequired}
                    placeholder={placeholder}
                    onChange={handleSsnChange}
                    onBlur={handleSsnBlur}
                    onFocus={handleSsnFocus}
                />
            </div>
        </Fragment>
    );
};

export default SocialSecurityNumberInput;
